import { Spin } from 'antd';
import { useEffect, useState } from 'react';

import BrowsedSinkItems from '../BrowsedSinkItems';
import SuppliedSinkItems from '../SuppliedSinkItems';
import * as St from './SidePanelSinksTab.style';
import { useAreSinksAvailable } from './SidePanelSinksTab.utils';

import { DesignSection, SINK_OPTIONS, UserDesignOperation } from '~/core/enums';
import { useDropAppliance } from '~/core/hooks/useDropAppliance';
import { useFilteredSink } from '~/core/hooks/useFilteredSink';
import { useGetDesignSectionTranslations } from '~/core/hooks/useGetDesignSectionTranslations';
import useLazyLoad from '~/core/hooks/useLazyLoad';
import { useRegisterUserAction } from '~/core/hooks/useRegisterUserAction';
import { useSink } from '~/core/hooks/useSink';
import { useTrendingLabels } from '~/core/hooks/useTrendingLabels';
import { ApplianceModel, ChipMinimizerModel } from '~/core/models';
import { useApplianceContext } from '~/legacy/easy-estimator-page/context';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { resetDraggedAppliance } from '~/redux/slices/draggedAppliance';
import {
  increaseSinkQuantity,
  selectSelectedBrowsingOption,
  updateSelectedSinkProductId,
} from '~/redux/slices/sink';
import SinkOptions from '~molecules/SinksOptions/SinksOptions.component';
import DesignSectionHeader from '~organisms/DesignSectionHeader';
import SinkInfoModal from '~organisms/SinkInfoModal';
import SortAndFiltersSection from '~organisms/SortAndFiltersSection';

const SidePanelSinksTab = () => {
  const dispatch = useAppDispatch();
  const { areSinksAvailable } = useAreSinksAvailable();
  const { isFetchingSinks } = useSink();
  const {
    filteredAndSortedSinks,
    sinkFilters,
    sortBy,
    selectedFilters,
    handleFiltersChange,
  } = useFilteredSink();

  const { getSortedByValue } = useTrendingLabels();

  const { updateOnDropAppliance } = useApplianceContext();

  const { registerUserAction } = useRegisterUserAction();

  const selectedSinkOption = useAppSelector(selectSelectedBrowsingOption);

  const [isSinkInfoModalVisible, setIsSinkInfoModalVisible] = useState(false);
  const [isDraggingSink, setIsDraggingSink] = useState(false);
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [selectedSink, setSelectedSink] = useState<ApplianceModel | undefined>(
    undefined
  );
  const [sortedByValue, setSortedByValue] = useState<string>(sortBy);

  const { resolveDropAppliance } = useDropAppliance();

  const { visibleItems, setObserverTarget } = useLazyLoad({
    itemCount: filteredAndSortedSinks.length,
  });

  useEffect(() => {
    [...document.querySelectorAll('.skeleton')]
      .slice(0, 1)
      .forEach((element) => setObserverTarget(element));
  }, [visibleItems, setObserverTarget]);

  useEffect(() => {
    const fetchSortedByValue = async () => {
      const value = await getSortedByValue(sortBy);

      setSortedByValue(value);
    };

    fetchSortedByValue();
  }, [sortBy, getSortedByValue]);

  /** Visible sinks */
  const [sinks, skeletonSinks] = [
    filteredAndSortedSinks.slice(0, visibleItems),
    filteredAndSortedSinks.slice(visibleItems),
  ];

  const handleOnSinkClick = (sink: ApplianceModel, isDragAndDrop = false) => {
    dispatch(updateSelectedSinkProductId(sink.productId));
    setSelectedSink(sink);
    setIsSinkInfoModalVisible(true);

    if (isDragAndDrop) {
      setIsDraggingSink(true);
    }
  };

  // This function is called when the user selects a sink from the modal
  const addSinkFromModal = (chipMinimizer?: ChipMinimizerModel) => {
    // If the sink is being dragged, we don't want to increase the quantity
    if (selectedSink?.productId && !isDraggingSink) {
      dispatch(
        increaseSinkQuantity({
          id: selectedSink.productId,
          hasChipMinimizer: !!chipMinimizer,
        })
      );
    }

    if (selectedSink?.productId && isDraggingSink) {
      // If the sink is being dragged, resolve
      resolveDropAppliance({ chipMinimizer });
      setIsDraggingSink(false);
    }

    setIsSinkInfoModalVisible(false);

    registerUserAction(UserDesignOperation.MODIFY_SINK);
  };

  const handleSinkInfoModalClose = () => {
    updateOnDropAppliance(undefined);
    dispatch(resetDraggedAppliance());
    setIsSinkInfoModalVisible(false);
    setIsDraggingSink(false);
  };

  const { title, description, sortAndFilters } =
    useGetDesignSectionTranslations(DesignSection.SINKS, ['SORT_AND_FILTERS']);

  if (!areSinksAvailable) {
    return null;
  }

  return (
    <>
      <DesignSectionHeader title={title} description={description} />
      <SinkOptions selectedOption={selectedSinkOption} />
      {sinks.length > 0 && (
        <>
          {selectedSinkOption === SINK_OPTIONS.BROWSE_SINKS && (
            <St.SortAndFiltersContainer>
              <SortAndFiltersSection
                sortedBy={sortedByValue}
                component="sink"
                title={sortAndFilters}
                data={{ filters: sinkFilters }}
                selectedFilterOptions={selectedFilters}
                onChange={handleFiltersChange}
                setIsFiltersOpen={setIsFiltersOpen}
                uiMode="collapsible-button"
                isDisabled={isFetchingSinks}
              />
            </St.SortAndFiltersContainer>
          )}
          {!isFiltersOpen && isFetchingSinks ? (
            <Spin style={{ fontSize: 24, marginLeft: '40%' }} />
          ) : (
            <>
              {selectedSinkOption === SINK_OPTIONS.BROWSE_SINKS ? (
                <BrowsedSinkItems
                  sinks={sinks}
                  onSinkClick={handleOnSinkClick}
                  skeletonSinks={skeletonSinks}
                />
              ) : (
                <SuppliedSinkItems
                  sinks={sinks}
                  onSinkClick={handleOnSinkClick}
                />
              )}
            </>
          )}
        </>
      )}
      {selectedSink && (
        <SinkInfoModal
          sink={selectedSink}
          isVisible={!!selectedSink && isSinkInfoModalVisible}
          onClose={handleSinkInfoModalClose}
          onSubmit={addSinkFromModal}
        />
      )}
    </>
  );
};

export default SidePanelSinksTab;
